import React, { useRef } from "react";
import { useMotionValue, useSpring } from "framer-motion";
import Typewriter from "typewriter-effect";
import HeroBgAnimation from "../HeroBgAnimation";
import {
  HeroContainer,
  HeroInner,
  LeftCol,
  Eyebrow,
  PulseDot,
  Name,
  NameWord,
  RoleRow,
  TypewriterWrap,
  Description,
  CTARow,
  PrimaryButton,
  GhostButton,
  MetaRow,
  MetaDot,
  RightCol,
  CardStage,
  PhotoGlow,
  PhotoRing,
  PhotoMask,
  Photo,
  Chip,
  ChipDot,
  ScrollCue,
  MarqueeWrap,
  MarqueeTrack,
  MarqueeItem,
} from "./HeroStyle";
import { FiArrowUpRight, FiMail, FiChevronDown } from "react-icons/fi";
import { Bio, skills } from "../../data/constants";
import profileImg from "../../images/profile.png";

const marqueeNames = [
  "Python",
  "JavaScript",
  "TypeScript",
  "React JS",
  "Django",
  "Laravel",
  "Node.js",
  "AWS",
  "Docker",
  "PostgreSQL",
  "Kubernetes",
  "PyTorch",
];

const allSkills = skills.flatMap((group) => group.skills);
const marqueeSkills = marqueeNames
  .map((name) => allSkills.find((s) => s.name === name))
  .filter(Boolean);

const HeroSection = () => {
  const stageRef = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springX = useSpring(rotateX, { stiffness: 150, damping: 20 });
  const springY = useSpring(rotateY, { stiffness: 150, damping: 20 });

  const handleMouseMove = (e) => {
    if (!stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotateY.set(px * 14);
    rotateX.set(-py * 14);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  const nameWords = Bio.name.split(" ");

  return (
    <HeroContainer id="top">
      <HeroBgAnimation />
      <HeroInner>
        <LeftCol>
          <Eyebrow initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <PulseDot /> Open to new opportunities &middot; {Bio.location}
          </Eyebrow>

          <Name>
            {nameWords.map((word, i) => (
              <NameWord
                key={word + i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
              >
                {word}
              </NameWord>
            ))}
          </Name>

          <RoleRow>
            I'm a
            <TypewriterWrap>
              <Typewriter
                options={{ strings: Bio.roles, autoStart: true, loop: true }}
              />
            </TypewriterWrap>
          </RoleRow>

          <Description initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
            {Bio.description}
          </Description>

          <CTARow initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.72 }}>
            <PrimaryButton href="#projects">
              View My Work <FiArrowUpRight />
            </PrimaryButton>
            <GhostButton href="#contact">
              <FiMail /> Let's Talk
            </GhostButton>
          </CTARow>

          <MetaRow initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.9 }}>
            <span>7+ yrs experience</span>
            <MetaDot />
            <span>27+ sites in production</span>
            <MetaDot />
            <span>ITIL 4 &amp; Security+ certified</span>
          </MetaRow>
        </LeftCol>

        <RightCol>
          <CardStage
            ref={stageRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            initial={{ opacity: 0, y: 30, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <PhotoGlow />
            <PhotoRing style={{ rotateX: springX, rotateY: springY }}>
              <PhotoMask>
                <Photo src={profileImg} alt={Bio.name} />
              </PhotoMask>
            </PhotoRing>

            <Chip
              style={{ top: -18, left: -18 }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChipDot $c="#34D399" /> 27+ Sites Live
            </Chip>
            <Chip
              style={{ bottom: -16, right: -16 }}
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
            >
              <ChipDot $c="#22D3EE" /> 10k+ Users Served
            </Chip>
          </CardStage>
        </RightCol>
      </HeroInner>

      <MarqueeWrap>
        <MarqueeTrack>
          {[...marqueeSkills, ...marqueeSkills].map((skill, i) => (
            <MarqueeItem key={skill.name + i}>
              <img src={skill.image} alt="" />
              {skill.name}
            </MarqueeItem>
          ))}
        </MarqueeTrack>
      </MarqueeWrap>

      <ScrollCue href="#experience">
        Scroll <FiChevronDown size={16} />
      </ScrollCue>
    </HeroContainer>
  );
};

export default HeroSection;
